<script setup lang="ts">
import useProductDoses from "@/dosage/composables/useProductDoses";
import {
    Combobox,
    ComboboxAnchor,
    ComboboxEmpty,
    ComboboxGroup,
    ComboboxInput,
    ComboboxItem,
    ComboboxItemIndicator,
    ComboboxList,
    ComboboxTrigger
} from "@/core/components/ui/combobox";
import {ProductDose} from "@/product/interfaces/ProductDose";
import {Check, Search} from "@lucide/vue";
import {cn} from "@/lib/utils";
import {dosageApi} from "@/container";
import {computed, ref, watch} from "vue";
import debounce from "lodash.debounce";

const doseModel = defineModel<ProductDose | null>({required: true, default: null});

const props = defineProps<{
    productId: number
}>();

const loading = ref(false);
const search = ref(doseModel.value?.singular_name ?? "");
const searchData = ref<ProductDose[] | null>(null);
const {data: defaultData} = useProductDoses();

watch(() => props.productId, (value) => {
    if (doseModel.value) {
        return;
    }

    dosageApi().productDose(value)
        .then(dose => {
            doseModel.value = dose;
        });
}, {immediate: true});

/**
 * Compute the available doses.
 * Prioritize search results over default data.
 */
const availableDoses = computed(() => {
    if (searchData.value !== null) {
        return searchData.value;
    }
    return defaultData?.value ?? [];
});

/**
 * Search for product doses matching the query.
 */
const searchDose = async (query: string): Promise<ProductDose[]> => {
    const response = await dosageApi().productDoses({query, limit: 10});
    return response || [];
};

/**
 * Perform the search with debouncing.
 */
const performSearch = async (value: string) => {
    if (loading.value) {
        return;
    }

    if (!value || value === doseModel.value?.singular_name) {
        searchData.value = null;
        return;
    }

    loading.value = true;

    try {
        searchData.value = await searchDose(value);
    } catch {
        searchData.value = [];
    } finally {
        loading.value = false;
    }
};

/**
 * Debounce the search with a 300ms delay.
 */
const debouncedSearch = debounce(performSearch, 300);

/**
 * Handle input event on the search field.
 */
const onSearchInput = (event: Event) => {
    const target = event.target as HTMLInputElement;
    search.value = target.value;
    debouncedSearch(search.value);
};

/**
 * Handle selection of a dose.
 */
const onDoseSelect = (dose: ProductDose | null) => {
    doseModel.value = dose;
    searchData.value = null;
    search.value = dose?.singular_name || '';
};

/**
 * Get the display value for the combobox input.
 */
const getDisplayValue = (dose: ProductDose | null): string => {
    return dose?.singular_name ?? '';
};
</script>

<template>
  <Combobox
    :model-value="doseModel"
    @update:model-value="(value) => onDoseSelect(value as ProductDose | null)"
  >
    <ComboboxAnchor as-child>
      <ComboboxTrigger class="flex items-center gap-1">
        <div class="relative w-full max-w-sm items-center border-1 border-black">
          <ComboboxInput
            class="focus-visible:ring-0 rounded-lg shadow-none h-10 capitalize"
            :class="{
              'pl-9': !doseModel,
            }"
            placeholder="Sélectionnez une forme..."
            :display-value="getDisplayValue"
            @input="onSearchInput"
          />
          <span v-if="!doseModel" class="absolute start-0 inset-y-0 flex items-center justify-center px-3">
            <Search class="size-4 text-muted-foreground" />
          </span>
        </div>
      </ComboboxTrigger>
    </ComboboxAnchor>

    <ComboboxList>
      <ComboboxEmpty>
        <div v-if="loading" class="flex items-center justify-center py-2">
          <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2" />
          Recherche en cours...
        </div>
        <div v-else>
          Aucune forme trouvée.
        </div>
      </ComboboxEmpty>

      <ComboboxGroup>
        <ComboboxItem
          v-for="dose in availableDoses"
          :key="dose.id"
          :value="dose"
          class="hover:bg-gray-100 cursor-pointer capitalize"
        >
          <div class="flex gap-1 items-center">
            <div v-if="dose.icon" class="[&_svg]:size-5" v-html="dose.icon" />
            {{ dose.singular_name }}
          </div>
          <ComboboxItemIndicator>
            <Check :class="cn('ml-auto h-4 w-4')" />
          </ComboboxItemIndicator>
        </ComboboxItem>
      </ComboboxGroup>
    </ComboboxList>
  </Combobox>
</template>

<style scoped>
</style>
